import { zodResolver } from '@hookform/resolvers/zod';
import { type BillingProfile, type Organization, taxIdTypesFor } from '@noconflict/contracts';
import { PlusIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  billingProfileErrorMessage,
  isSavedButNotSynced,
  useSaveBillingProfile,
} from '../../../entities/billing-profile';
import { Button } from '../../../shared/ui/button';
import { Checkbox } from '../../../shared/ui/checkbox';
import { CountrySelect } from '../../../shared/ui/country-select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../shared/ui/form';
import { Input as TextInput } from '../../../shared/ui/input';
import { Notice } from '../../../shared/ui/notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../shared/ui/select';
import { toast } from '../../../shared/ui/sonner';
import {
  type BillingProfileFormInput,
  type BillingProfileFormValues,
  billingProfileFormSchema,
  MAX_BILLING_EMAILS,
  NO_TAX_ID,
  toDefaults,
  toInput,
} from './model';

type TaxIdLabelKey = `settings.billing.taxIdTypes.${'us_ein' | 'eu_vat'}`;

/**
 * The org billing profile: legal entity, address, optional tax ID limited to the types the
 * payment provider supports for the country, billing emails, and "require a reference on every order".
 */
export function BillingProfileForm({
  org,
  profile,
  onSaved,
  onCancel,
}: {
  org: Organization;
  profile: BillingProfile | null;
  /** Called once the details are saved, including when only the payment provider sync failed (it is retried before payment). */
  onSaved?: () => void;
  /** Shows a Cancel button beside Save (the form in a dialog). */
  onCancel?: () => void;
}) {
  const { t } = useTranslation('org');
  const save = useSaveBillingProfile(org.id);
  const [error, setError] = useState<string | null>(null);
  const [notSynced, setNotSynced] = useState(false);
  const form = useForm<BillingProfileFormInput, unknown, BillingProfileFormValues>({
    resolver: zodResolver(billingProfileFormSchema),
    defaultValues: toDefaults(profile, org.country),
  });
  const emails = useFieldArray({ control: form.control, name: 'billingEmails' });
  const country = form.watch('country');
  const taxIdType = form.watch('taxIdType');
  const types = taxIdTypesFor(country);
  const typeFits = taxIdType !== NO_TAX_ID && types.includes(taxIdType);

  return (
    <Form {...form}>
      <form
        className="grid gap-6"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setError(null);
          setNotSynced(false);
          try {
            await save.mutateAsync(toInput(values));
            toast.success(t('settings.billing.saved'));
            onSaved?.();
          } catch (e) {
            if (isSavedButNotSynced(e)) {
              setNotSynced(true);
              onSaved?.();
            } else setError(billingProfileErrorMessage(e));
          }
        })}
      >
        <FormField
          control={form.control}
          name="legalName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settings.billing.legalName')}</FormLabel>
              <FormControl>
                <TextInput autoComplete="organization" {...field} />
              </FormControl>
              <FormDescription>{t('settings.billing.legalNameHelp')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid items-start gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="line1"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t('settings.billing.line1')}</FormLabel>
                <FormControl>
                  <TextInput autoComplete="address-line1" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="line2"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t('settings.billing.line2')}</FormLabel>
                <FormControl>
                  <TextInput autoComplete="address-line2" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('settings.billing.city')}</FormLabel>
                <FormControl>
                  <TextInput autoComplete="address-level2" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="region"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('settings.billing.region')}</FormLabel>
                <FormControl>
                  <TextInput autoComplete="address-level1" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="postalCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('settings.billing.postalCode')}</FormLabel>
                <FormControl>
                  <TextInput autoComplete="postal-code" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('settings.billing.country')}</FormLabel>
                <FormControl>
                  <CountrySelect
                    value={field.value}
                    onValueChange={(next) => {
                      field.onChange(next);
                      // a tax ID type belongs to one country
                      if (!taxIdTypesFor(next).includes(form.getValues('taxIdType')))
                        form.setValue('taxIdType', NO_TAX_ID);
                    }}
                  />
                </FormControl>
                <FormDescription>{t('settings.billing.countryHelp')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <fieldset className="grid gap-4">
          <legend className="mb-2 font-bold text-heading">
            {t('settings.billing.taxIdTitle')}
          </legend>
          {types.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('settings.billing.taxIdUnavailable')}
            </p>
          ) : (
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="taxIdType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings.billing.taxIdType')}</FormLabel>
                    <Select
                      value={typeFits ? field.value : NO_TAX_ID}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={NO_TAX_ID}>{t('settings.billing.taxIdNone')}</SelectItem>
                        {types.map((type) => (
                          <SelectItem key={type} value={type}>
                            {t(`settings.billing.taxIdTypes.${type}` as TaxIdLabelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {typeFits ? (
                <FormField
                  control={form.control}
                  name="taxIdValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('settings.billing.taxIdValue')}</FormLabel>
                      <FormControl>
                        <TextInput autoComplete="off" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
            </div>
          )}
        </fieldset>

        <fieldset className="grid gap-3">
          <legend className="mb-1 font-bold text-heading">
            {t('settings.billing.emailsTitle')}
          </legend>
          <p className="text-sm text-muted-foreground">{t('settings.billing.emailsHelp')}</p>
          {emails.fields.map((row, index) => (
            <FormField
              key={row.id}
              control={form.control}
              name={`billingEmails.${index}.value`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {t('settings.billing.email', { n: index + 1 })}
                  </FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <TextInput type="email" autoComplete="off" {...field} />
                    </FormControl>
                    {emails.fields.length > 1 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('settings.billing.removeEmail', { n: index + 1 })}
                        onClick={() => emails.remove(index)}
                      >
                        <XIcon aria-hidden="true" />
                      </Button>
                    ) : null}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
          {emails.fields.length < MAX_BILLING_EMAILS ? (
            <div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => emails.append({ value: '' })}
              >
                <PlusIcon aria-hidden="true" />
                {t('settings.billing.addEmail')}
              </Button>
            </div>
          ) : null}
        </fieldset>

        <FormField
          control={form.control}
          name="poRequired"
          render={({ field }) => (
            <FormItem className="flex items-start gap-3">
              <FormControl>
                <Checkbox
                  className="mt-1"
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                />
              </FormControl>
              <div className="grid gap-1">
                <FormLabel>{t('settings.billing.poRequired')}</FormLabel>
                <FormDescription>{t('settings.billing.poRequiredHelp')}</FormDescription>
              </div>
            </FormItem>
          )}
        />

        {notSynced ? <Notice tone="warning">{t('settings.billing.savedNotSynced')}</Notice> : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={save.isPending}>
            {t('settings.billing.save')}
          </Button>
          {onCancel ? (
            <Button type="button" variant="secondary" onClick={onCancel}>
              {t('settings.billing.cancel')}
            </Button>
          ) : null}
        </div>
      </form>
    </Form>
  );
}
